// Paw Pounce — a tiny demo that exercises every game-kit module on a bare Canvas 2D.
// The kit provides feel (loop/input/audio/save/leaderboard); the canvas drawing here is
// the game's own render layer, proving the kit stays out of the renderer's way.

import {
  createLoop,
  attachInput,
  haptic,
  createAudio,
  createSave,
  createLeaderboard,
} from '../src/index.js';

// ---- DOM ----
const canvas = document.getElementById('stage') as HTMLCanvasElement;
const ctx = canvas.getContext('2d')!;
const scoreEl = document.getElementById('score')!;
const bestEl = document.getElementById('best')!;
const nameEl = document.getElementById('name') as HTMLInputElement;
const soundBtn = document.getElementById('sound') as HTMLButtonElement;
const boardBtn = document.getElementById('boardBtn') as HTMLButtonElement;
const boardEl = document.getElementById('board') as HTMLDivElement;
const boardList = document.getElementById('boardList') as HTMLOListElement;
const boardClose = document.getElementById('boardClose') as HTMLButtonElement;
const hintEl = document.getElementById('hint')!;

// ---- viewport (handle DPR + resize) ----
let W = 0;
let H = 0;
let dpr = 1;
function resize() {
  dpr = Math.min(window.devicePixelRatio || 1, 3);
  W = window.innerWidth;
  H = window.innerHeight;
  canvas.width = Math.round(W * dpr);
  canvas.height = Math.round(H * dpr);
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}
window.addEventListener('resize', resize);
resize();

// ---- kit: save ----
const save = createSave('gamekit_pawpounce', { best: 0, name: '', sound: true });
nameEl.value = save.get('name');
bestEl.textContent = String(save.get('best'));
nameEl.addEventListener('change', () => save.set('name', nameEl.value.trim().slice(0, 24)));

// ---- kit: audio ----
const audio = createAudio({
  enabled: save.get('sound'),
  sounds: {
    bounce: [{ freq: 220, to: 160, dur: 0.09, type: 'triangle', vol: 0.14 }],
    tap: [
      { freq: 520, dur: 0.05, type: 'square', vol: 0.1 },
      { freq: 780, start: 0.05, dur: 0.07, type: 'square', vol: 0.1 },
    ],
    fling: [{ freq: 300, to: 760, dur: 0.24, type: 'triangle', vol: 0.13 }],
    land: [
      { freq: 140, to: 70, dur: 0.22, type: 'sine', vol: 0.16 },
      { freq: 90, start: 0.02, dur: 0.2, type: 'square', vol: 0.06 },
    ],
    best: [
      { freq: 523, dur: 0.09, type: 'square', vol: 0.12 },
      { freq: 784, start: 0.09, dur: 0.14, type: 'square', vol: 0.12 },
    ],
  },
});
soundBtn.textContent = audio.enabled ? '🔊' : '🔇';
soundBtn.addEventListener('click', () => {
  audio.setEnabled(!audio.enabled);
  save.set('sound', audio.enabled);
  soundBtn.textContent = audio.enabled ? '🔊' : '🔇';
});

// ---- kit: leaderboard (local-only for the demo — no D1 wired yet) ----
const board = createLeaderboard({ localOnly: true, localKey: 'gamekit_pawpounce_board' });
const ROUTE = 'Paw Pounce';
function renderBoard(entries: { name: string; score: number }[]) {
  boardList.innerHTML =
    entries.length === 0
      ? '<li class="empty">No scores yet — go pounce.</li>'
      : entries
          .map((e) => `<li><span>${escapeHtml(e.name)}</span><b>${e.score}</b></li>`)
          .join('');
}
function escapeHtml(s: string) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
boardBtn.addEventListener('click', async () => {
  renderBoard((await board.fetch(ROUTE)) as { name: string; score: number }[]);
  boardEl.hidden = false;
});
boardClose.addEventListener('click', () => (boardEl.hidden = true));

// ---- game state ----
const R = 26; // paw radius
const GRAV = 2100; // px/s^2
const state = {
  x: 0,
  y: 0,
  vx: 0,
  vy: 0,
  px: 0,
  py: 0, // previous-step position (for render interpolation)
  score: 0,
  alive: false,
  flash: 0, // bounce squash timer
  spin: 0,
};
function reset() {
  state.x = W / 2;
  state.y = H * 0.35;
  state.px = state.x;
  state.py = state.y;
  state.vx = 0;
  state.vy = 0;
  state.score = 0;
  state.alive = true;
  state.flash = 0;
  state.spin = 0;
  scoreEl.textContent = '0';
}
reset();

function bumpScore() {
  state.score++;
  scoreEl.textContent = String(state.score);
}

async function land() {
  if (!state.alive) return;
  state.alive = false;
  audio.play('land');
  haptic([30, 40, 30]);
  const best = save.get('best');
  if (state.score > best) {
    save.set('best', state.score);
    bestEl.textContent = String(state.score);
    audio.play('best');
    const name = (nameEl.value.trim() || 'you').slice(0, 24);
    await board.submit({ name, score: state.score, route: ROUTE });
  }
  setTimeout(reset, 650);
}

// ---- kit: input ----
attachInput(canvas, {
  swipeThreshold: 26,
  onTap: (p) => {
    hintEl.classList.add('gone');
    if (!state.alive) return;
    // pop the paw upward, nudged toward the tap horizontally
    state.vy = -900;
    state.vx += (p.x - state.x) * 3.2;
    state.vx = Math.max(-900, Math.min(900, state.vx));
    state.flash = 1;
    bumpScore();
    audio.play('tap');
    haptic(8);
  },
  onSwipe: (e) => {
    hintEl.classList.add('gone');
    if (!state.alive) return;
    state.vx = Math.max(-1400, Math.min(1400, e.dx * 9));
    state.vy = Math.max(-1400, Math.min(0, e.dy * 9 - 200));
    state.flash = 1;
    bumpScore();
    audio.play('fling');
    haptic(12);
  },
});

// ---- kit: loop (fixed timestep; render interpolates with alpha) ----
const loop = createLoop({
  step: 1 / 120,
  update: (dt) => {
    if (state.flash > 0) state.flash = Math.max(0, state.flash - dt * 6);
    if (!state.alive) return;
    state.px = state.x;
    state.py = state.y;
    state.vy += GRAV * dt;
    state.x += state.vx * dt;
    state.y += state.vy * dt;
    state.spin += state.vx * dt * 0.01;

    // walls
    if (state.x < R) {
      state.x = R;
      state.vx = Math.abs(state.vx) * 0.7;
      if (Math.abs(state.vx) > 60) audio.play('bounce');
    } else if (state.x > W - R) {
      state.x = W - R;
      state.vx = -Math.abs(state.vx) * 0.7;
      if (Math.abs(state.vx) > 60) audio.play('bounce');
    }
    // ceiling
    if (state.y < R) {
      state.y = R;
      state.vy = Math.abs(state.vy) * 0.6;
    }
    // floor = lose
    if (state.y > H - R) {
      state.y = H - R;
      void land();
    }
  },
  render: (alpha) => {
    // interpolate between previous and current step for buttery motion at any refresh rate
    const ix = state.px + (state.x - state.px) * alpha;
    const iy = state.py + (state.y - state.py) * alpha;
    draw(ix, iy);
  },
});

function draw(x: number, y: number) {
  ctx.clearRect(0, 0, W, H);

  // floor danger line
  ctx.strokeStyle = 'rgba(255,90,90,0.35)';
  ctx.lineWidth = 2;
  ctx.setLineDash([8, 8]);
  ctx.beginPath();
  ctx.moveTo(0, H - 3);
  ctx.lineTo(W, H - 3);
  ctx.stroke();
  ctx.setLineDash([]);

  // squash-and-stretch from the flash timer (juice)
  const sq = 1 + state.flash * 0.35;
  const st = 1 - state.flash * 0.25;

  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(state.spin);
  ctx.scale(st, sq);

  // paw
  ctx.fillStyle = state.alive ? '#ffcf5c' : '#6b7280';
  ctx.beginPath();
  ctx.arc(0, 0, R, 0, Math.PI * 2);
  ctx.fill();
  // toe beans
  ctx.fillStyle = 'rgba(0,0,0,0.35)';
  ctx.beginPath();
  ctx.arc(0, 4, R * 0.42, 0, Math.PI * 2);
  ctx.fill();
  for (let i = -1; i <= 1; i++) {
    ctx.beginPath();
    ctx.arc(i * R * 0.42, -R * 0.4, R * 0.2, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

loop.start();
